import type { Transition, Variants } from 'framer-motion';

export const duration = {
  instant: 0.1,
  fast: 0.15,
  base: 0.2,
  slow: 0.3,
  slower: 0.45,
  cinematic: 0.6,
} as const;

export const easing = {
  standard: [0.2, 0, 0, 1] as const,
  emphasized: [0.3, 0, 0, 1] as const,
  decelerate: [0, 0, 0, 1] as const,
  accelerate: [0.3, 0, 1, 1] as const,
  anticipate: [0.4, 0, 0.2, 1.4] as const,
} as const;

export const spring = {
  gentle: { type: 'spring', stiffness: 170, damping: 22, mass: 1 } satisfies Transition,
  responsive: { type: 'spring', stiffness: 340, damping: 30, mass: 1 } satisfies Transition,
  snappy: { type: 'spring', stiffness: 520, damping: 34, mass: 1 } satisfies Transition,
  bouncy: { type: 'spring', stiffness: 380, damping: 18, mass: 1.1 } satisfies Transition,
  soft: { type: 'spring', stiffness: 220, damping: 28, mass: 1.2 } satisfies Transition,
} as const;

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: duration.base, ease: easing.standard } },
  exit: { opacity: 0, transition: { duration: duration.fast, ease: easing.standard } },
};

export const scalePop: Variants = {
  hidden: { opacity: 0, scale: 0.94, y: 8 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: spring.responsive,
  },
  exit: {
    opacity: 0,
    scale: 0.96,
    y: 4,
    transition: { duration: duration.fast, ease: easing.accelerate },
  },
};

export const slideInRight: Variants = {
  hidden: { x: '100%' },
  visible: { x: 0, transition: spring.soft },
  exit: { x: '100%', transition: { duration: duration.slow, ease: easing.accelerate } },
};

export const slideUp: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: spring.gentle },
  exit: { opacity: 0, y: 8, transition: { duration: duration.fast, ease: easing.accelerate } },
};

export const slideDown: Variants = {
  hidden: { opacity: 0, y: -12 },
  visible: { opacity: 1, y: 0, transition: spring.gentle },
  exit: { opacity: 0, y: -6, transition: { duration: duration.fast, ease: easing.accelerate } },
};

export const backdropFade: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: duration.slow, ease: easing.decelerate } },
  exit: { opacity: 0, transition: { duration: duration.base, ease: easing.accelerate } },
};

export const listStagger = (stagger = 0.04, delay = 0): Variants => ({
  hidden: {},
  visible: {
    transition: { staggerChildren: stagger, delayChildren: delay },
  },
});

export const listItem: Variants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: spring.gentle },
};

export const pressable = {
  whileHover: { y: -1 },
  whileTap: { scale: 0.97 },
  transition: spring.snappy,
} as const;

export const iconPulse: Variants = {
  hidden: { scale: 0.6, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { ...spring.bouncy, delay: 0.08 },
  },
};
